/**
 * Color palette for Account section — resolves to the same `--pop-*` CSS
 * variables as the quiz design tokens (src/theme/quizTokens.ts), so the
 * account app always matches the quiz palette. Hex fallbacks mirror the
 * quiz token fallbacks.
 */

export const ACCOUNT_COLORS = {
  // Primary brand colors
  primary: "var(--pop-brown, #4a3026)", // Brown (buttons, headings, primary actions)
  primaryDark: "var(--pop-ink, #332115)", // Ink (text, secondary elements)
  primaryHover: "var(--pop-ink, #332115)", // Hover state for primary buttons

  // Background colors
  cream: "var(--pop-cream, #faf5eb)", // Cream background
  white: "#FFFFFF", // Pure white

  // Accent colors
  coral: "var(--pop-blue, #8ee5ff)", // Quiz accent (sliders, highlights)
  coralLight: "var(--pop-cream-2, #e9dfd7)", // Soft neutral tint
  green: "var(--pop-green, #cced85)", // Green (success, selected states)
  greenHover: "#B9E46C", // Green hover
  lavender: "var(--pop-cream-2, #e9dfd7)", // Card/background tint
  blue: "var(--pop-blue, #8ee5ff)", // Blue (info cards)
  blueLight: "var(--pop-blue, #8ee5ff)", // Blue accent

  // Text colors
  textPrimary: "var(--pop-brown, #4a3026)", // Dark brown text
  textSecondary: "var(--pop-grey, #3e3e3e)", // Muted grey-brown
  textTertiary: "#9098a3", // Muted stat-label grey (quiz statLabel)
  textDisabled: "#AAAAAA", // Disabled text

  // State colors
  success: "var(--pop-green, #cced85)", // Success/completed
  error: "#c62828", // Error (quiz error red)
  warning: "#CE7A00", // Warning amber
  info: "var(--pop-blue, #8ee5ff)", // Info

  // Border colors
  borderLight: "var(--pop-cream-3, #d7c7b4)", // Light borders (quiz input borders)
  borderMedium: "var(--pop-cream-3, #d7c7b4)", // Medium borders
  borderDark: "var(--pop-brown, #4a3026)", // Dark borders

  // Component-specific
  sliderTrack: "var(--pop-blue, #8ee5ff)", // Slider track (quiz slider blue)
  sliderThumb: "var(--pop-blue, #8ee5ff)", // Slider thumb
  cardSelected: "var(--pop-green, #cced85)", // Selected card background
  cardHover: "var(--pop-cream-2, #e9dfd7)", // Card hover background
};

/**
 * MUI theme extensions for account section
 */
export const getAccountTheme = (theme: any) => ({
  ...theme,
  palette: {
    ...theme.palette,
    primary: {
      main: ACCOUNT_COLORS.primary,
      dark: ACCOUNT_COLORS.primaryDark,
      contrastText: ACCOUNT_COLORS.white,
    },
    secondary: {
      main: ACCOUNT_COLORS.green,
      dark: ACCOUNT_COLORS.greenHover,
      contrastText: ACCOUNT_COLORS.primaryDark,
    },
    success: {
      main: ACCOUNT_COLORS.success,
    },
    error: {
      main: ACCOUNT_COLORS.error,
    },
    warning: {
      main: ACCOUNT_COLORS.warning,
    },
    info: {
      main: ACCOUNT_COLORS.info,
    },
    background: {
      default: ACCOUNT_COLORS.cream,
      paper: ACCOUNT_COLORS.white,
    },
    text: {
      primary: ACCOUNT_COLORS.textPrimary,
      secondary: ACCOUNT_COLORS.textSecondary,
    },
  },
});
